import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { collectionFeature, CollectionFetch } from '@workspaces/store/collection.feature';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatOption } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { WorkspaceDocsCollectionsService } from '@workspaces/services/document.service';
import { collectionDocFeature, CollectionDocFetch } from '@workspaces/store/collection-doc.feature';
import { MatTableModule } from '@angular/material/table';
import { ProcessingStagePipe } from '../../utils/pipes/processing-stage.pipe';
import { CatalogueValuePipe } from '../../utils/pipes/catalog-value.pipe';
import { StatusColorPipe } from '../../utils/pipes/status-color.pipe';
import { LoaderButtonComponent } from '../loader-button/loader-button.component';
import { workspaceSourceDocFeature, WorkspaceSourceDocFetch } from '@workspaces/store/workspace-source-doc.feature';
import { Collection, FileMetadata } from 'src/app/models/collection.interface';

@Component({
  selector: 'app-copy-collection-modal',
  templateUrl: './copy-collection-modal.component.html',
  styleUrls: ['./copy-collection-modal.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatProgressBar,
    MatOption,
    CommonModule,
    ReactiveFormsModule,
    MatSelect,
    MatTableModule,
    ProcessingStagePipe,
    CatalogueValuePipe,
    StatusColorPipe,
    LoaderButtonComponent
  ]
})
export class CopyCollectionModalComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public readonly displayedColumns = ['title', 'year', 'processing_stage', 'file_id'] as const;

  public collectionIsLoading$!: Observable<boolean>;
  public availableCollections$!: Observable<Collection[]>;
  public collectionFiles$!: Observable<FileMetadata[]>;
  public collectionDocsIsLoading$!: Observable<boolean>;
  public isPending = false;
  public selectedCollectionId = new FormControl('', Validators.required);
  constructor(
    private readonly store: Store<AppState>,
    private readonly dialogRef: MatDialogRef<CopyCollectionModalComponent>,
    private readonly workspaceDocsCollectionsService: WorkspaceDocsCollectionsService,
    @Inject(MAT_DIALOG_DATA) private readonly workspaceId: string
  ) {}

  ngOnInit(): void {
    this.store.dispatch(CollectionFetch({ includeHidden: false }));
    this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
    this.collectionIsLoading$ = this.store.select(collectionFeature.collectionIsLoading);
    this.availableCollections$ = combineLatest([
      this.store.select(collectionFeature.selectData),
      this.store.select(workspaceSourceDocFeature.workspaceSourceDocData(this.workspaceId))
    ]).pipe(
      map(([collections, workspaceFiles]) => {
        return collections.filter(collection =>
          workspaceFiles.collections.every(workspaceCollection => workspaceCollection.id !== collection.collection_id)
        );
      })
    );

    this.sub.add(
      this.selectedCollectionId.valueChanges.subscribe(collectionId => {
        if (collectionId) {
          this.store.dispatch(CollectionDocFetch({ collectionId }));
          this.collectionFiles$ = this.store.select(collectionDocFeature.collectionDocData(collectionId));
          this.collectionDocsIsLoading$ = this.store.select(collectionDocFeature.collectionDocIsLoading(collectionId));
        }
      })
    );
  }

  onConfirm() {
    if (!this.selectedCollectionId.valid) {
      this.selectedCollectionId.markAllAsTouched();
      return;
    }
    if (!this.selectedCollectionId.value) {
      throw Error('Missing selectedCollection value');
    }
    this.isPending = true;
    //Don't need ngrx store as this is a one time closeable modal
    this.sub.add(
      this.workspaceDocsCollectionsService
        .linkCollections(this.workspaceId, { collection_ids: [this.selectedCollectionId.value] })
        .subscribe({
          next: () => {
            this.isPending = false;
            this.dialogRef.close();
          },
          error: () => {
            this.isPending = false;
            this.dialogRef.close();
          }
        })
    );
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
